import { CheckCircle2, Loader2, Circle, XCircle } from 'lucide-react';
import type { ActiveResearchTask } from './canvas-types';

interface TaskProgressCardProps {
  task: ActiveResearchTask;
  onViewReport: () => void;
  onSendMessage: (msg: string) => void;
  isTyping?: boolean;
}

function getToolLabel(tool: string, description: string): string {
  if (description) return description;
  const labels: Record<string, string> = {
    CHECK_HEARTBEAT: 'Check existing data',
    SCRAPE_AND_ANALYZE_BRAND: 'Analyze brand website',
    SCRAPE_WEBSITE: 'Scrape website',
    ANALYZE_BRAND_DNA: 'Analyze brand DNA',
    SEARCH_AD_LIBRARY: 'Search ad library',
    ANALYZE_AD_CREATIVES: 'Analyze ad creatives',
    DISCOVER_COMPETITORS: 'Discover competitors',
    RESEARCH_SENTIMENT: 'Research customer sentiment',
    GENERATE_REPORT: 'Generate research report',
    SAVE_TO_PLATFORM: 'Save to platform',
  };
  return labels[tool] || tool;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function TaskProgressCard({ task, onSendMessage: _onSendMessage, isTyping: _isTyping, onViewReport: _onViewReport }: TaskProgressCardProps) {
  const { plan, status } = task;
  const isFailed = status === 'failed';

  return (
    <div
      data-testid="task-progress-card"
      style={{ marginTop: 8 }}
    >
      {/* Steps list — inline, no card wrapper */}
      <div style={{ position: 'relative', paddingLeft: 20 }}>
        {/* Vertical connector line */}
        <div
          style={{
            position: 'absolute',
            left: 6,
            top: 8,
            bottom: 8,
            width: 1,
            background: 'var(--color-border-tertiary, #e5e7eb)',
          }}
        />

        {plan.steps.filter((s) => s.status !== 'pending').map((step, idx, visibleSteps) => {
          const isStepRunning = step.status === 'running';
          const isStepDone = step.status === 'completed';
          const isStepFailed = step.status === 'failed';
          const isStepPending = false;
          const isLast = idx === visibleSteps.length - 1;

          return (
            <div
              key={step.id}
              data-testid={`step-item-${step.id}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                paddingBottom: isLast ? 0 : 8,
                position: 'relative',
              }}
            >
              {/* Status icon — overlaid on connector line */}
              <span
                style={{
                  flexShrink: 0,
                  marginTop: 1,
                  position: 'absolute',
                  left: -14,
                  background: 'var(--color-background-primary, #F7F9FC)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 14,
                  height: 14,
                }}
              >
                {isStepRunning && (
                  <Loader2 size={12} style={{ color: 'var(--color-text-tertiary, #9ca3af)', animation: 'spin 1s linear infinite' }} />
                )}
                {isStepDone && <CheckCircle2 size={12} style={{ color: 'var(--color-text-tertiary, #9ca3af)' }} />}
                {isStepFailed && <XCircle size={12} style={{ color: '#dc2626' }} />}
                {isStepPending && <Circle size={12} style={{ color: 'var(--color-border-tertiary, #e5e7eb)' }} strokeWidth={1.5} />}
              </span>

              {/* Description + duration */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    fontSize: 13,
                    color: isStepFailed
                      ? '#dc2626'
                      : isStepPending
                      ? 'var(--color-text-tertiary, #9ca3af)'
                      : 'var(--color-text-secondary, #6b7280)',
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {getToolLabel(step.tool, step.description)}
                </span>

                {/* Duration badge */}
                {step.durationMs !== undefined && isStepDone && (
                  <span
                    data-testid={`duration-${step.id}`}
                    style={{
                      fontSize: 11,
                      color: 'var(--color-text-tertiary, #9ca3af)',
                      flexShrink: 0,
                    }}
                  >
                    {formatDuration(step.durationMs)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {isFailed && task.errorMessage && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: '#dc2626',
          }}
        >
          Error: {task.errorMessage}
        </div>
      )}
    </div>
  );
}
